import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import CategoryDetail from './components/CategoryDetail';
import Checkout from './components/Checkout';
import PaymentSelection from './components/PaymentSelection';
import CardPayment from './components/CardPayment';
import Wheelbarrow from './components/Wheelbarrow';
import OwnerPage from './components/OwnerPage';
import Login from './components/Login';
import AuthCallback from './components/AuthCallback';
import UserProfile from './components/UserProfile';
import MyItems from './components/MyItems'; // Keep this since it's used in routes
import AddItem from './components/AddItem';
import EditItem from './components/EditItem';
import MyRentals from './components/MyRentals';
import RentalDetails from './components/RentalDetails';
import Notifications from './components/Notifications';
import Help from './components/Help';
import About from './components/About';
import { WheelbarrowProvider } from './context/WheelbarrowContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DataProvider } from './context/DataContext';

// Lazy load the ItemMessages component
const ItemMessages = React.lazy(() => import('./components/ItemMessages'));

// Protected Route wrapper component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: window.location.pathname }} />;
  }

  return <>{children}</>;
};

function AppContent() {
  return (
    <div className="app">
      <Header />
      <div className="app-content">
        <Sidebar />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Navigate to="/category/all" replace />} />
            <Route path="/category/:categoryId" element={<CategoryDetail />} />
            <Route path="/checkout" element={
              <ProtectedRoute>
                <Checkout />
              </ProtectedRoute>
            } />
            <Route path="/payment-selection" element={
              <ProtectedRoute>
                <PaymentSelection />
              </ProtectedRoute>
            } />
            <Route path="/payment/card" element={
              <ProtectedRoute>
                <CardPayment />
              </ProtectedRoute>
            } />
            <Route path="/owner/:ownerId" element={<OwnerPage />} />
            <Route path="/owner/:ownerId/items" element={<OwnerPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/profile" element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            } />
            <Route path="/my-items" element={
              <ProtectedRoute>
                <MyItems />
              </ProtectedRoute>
            } />
            <Route path="/add-item" element={
              <ProtectedRoute>
                <AddItem />
              </ProtectedRoute>
            } />
            <Route path="/edit-item/:itemId" element={
              <ProtectedRoute>
                <EditItem />
              </ProtectedRoute>
            } />

            <Route path="/my-rentals" element={
              <ProtectedRoute>
                <MyRentals />
              </ProtectedRoute>
            } />
            <Route path="/rentals/:rentalId" element={
              <ProtectedRoute>
                <RentalDetails />
              </ProtectedRoute>
            } />
            <Route path="/notifications" element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            } />
            <Route path="/item/:itemId/messages" element={
              <ProtectedRoute>
                <Suspense fallback={<div>Loading...</div>}>
                  <ItemMessages />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/help" element={<Help />} />
            <Route path="/about" element={<About />} />
          </Routes>
        </main>
        <Wheelbarrow />
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <WheelbarrowProvider>
          <Router>
            <AppContent />
          </Router>
        </WheelbarrowProvider>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;
